/**
 * LOG-20 — browser error reporting, off unless a DSN is configured.
 *
 * Inert until VITE_SENTRY_DSN is set in client/.env and the client is
 * rebuilt. Unlike the server half, enabling this needs a rebuild, since
 * Vite bakes env values into the bundle at build time.
 *
 * The SDK is loaded with a dynamic import() rather than a static one, so
 * Vite splits it into its own chunk. With no DSN configured that chunk
 * is emitted but never requested — visitors download none of it. A
 * static import would put ~30 kB gzipped into the main bundle for every
 * visitor whether or not reporting was ever switched on.
 *
 * Because loading is asynchronous, the very first error on a cold page
 * may be reported slightly after it happens, or missed entirely if the
 * page is closed immediately. That is an acceptable trade for not
 * taxing every page load with a dependency that is off by default.
 *
 * TO ENABLE
 *   1. Create a project at sentry.io and copy its DSN.
 *   2. Add to client/.env:   VITE_SENTRY_DSN=<paste it>
 *   3. Rebuild:              cd client && npm run build
 *
 * Use a SEPARATE Sentry project from the server's. Browser errors and
 * API errors have completely different triage paths, and mixing them
 * makes both harder to read.
 */

const DSN = import.meta.env.VITE_SENTRY_DSN || '';

/** Resolves to the SDK module once loaded, or null when disabled. */
let sentryPromise = null;

function loadSentry() {
  if (!DSN) return null;
  if (sentryPromise) return sentryPromise;

  sentryPromise = import('@sentry/react')
    .then((Sentry) => {
      Sentry.init({
        dsn: DSN,
        environment: import.meta.env.MODE,
        // Error reporting only. Tracing and session replay sample real
        // traffic and exhaust the free tier quickly.
        tracesSampleRate: 0,
        // Browsers are noisy. These are the usual suspects that generate
        // events nobody can act on: extensions injecting scripts, and
        // network blips on third-party embeds.
        ignoreErrors: [
          'ResizeObserver loop limit exceeded',
          'ResizeObserver loop completed with undelivered notifications',
          'Non-Error promise rejection captured',
        ],
        denyUrls: [/extensions\//i, /^chrome:\/\//i, /^moz-extension:\/\//i],
      });
      return Sentry;
    })
    .catch(() => {
      // Reporting failing to load must never affect the page. Give up
      // quietly and stop retrying.
      sentryPromise = null;
      return null;
    });

  return sentryPromise;
}

/**
 * Reports an error if a DSN is configured; otherwise does nothing.
 * Never throws and never returns a rejected promise.
 *
 * @param {Error} error
 * @param {object} [context] extra fields attached to the event
 */
export function captureException(error, context) {
  const pending = loadSentry();
  if (!pending) return;

  pending
    .then((Sentry) => {
      if (Sentry) Sentry.captureException(error, context ? { extra: context } : undefined);
    })
    .catch(() => {
      /* swallow — reporting is best effort */
    });
}

/** Whether a DSN is configured. */
export const isSentryConfigured = () => Boolean(DSN);
